import React, { useState, useEffect } from 'react';
import './MasterDuelTimer.css';
import CalculatorModal from '../components/CalculatorModal';

const MasterDuelTimer = ({ initialTime: defaultInitialTime = 480, setBackButtonDisabled }) => {
    const [chosenInitialTime, setChosenInitialTime] = useState(defaultInitialTime);
    const [player1Time, setPlayer1Time] = useState(null);
    const [player2Time, setPlayer2Time] = useState(null);
    const [activePlayer, setActivePlayer] = useState(null);
    const [running, setRunning] = useState(false);
    const [paused, setPaused] = useState(false);
    const [isNew, setIsNew] = useState(true);
    const [isLoaded, setIsLoaded] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [pendingEndPhasePlayer, setPendingEndPhasePlayer] = useState(null);

    const [player1LP, setPlayer1LP] = useState(8000);
    const [player2LP, setPlayer2LP] = useState(8000);
    const [player1Input, setPlayer1Input] = useState("");
    const [player2Input, setPlayer2Input] = useState("");
    const [player1Operator, setPlayer1Operator] = useState("+");
    const [player2Operator, setPlayer2Operator] = useState("+");

    const [showCalculator, setShowCalculator] = useState(false);

    const [startTurnUsed, setStartTurnUsed] = useState(false);



    const cappedAdd = (current, add) =>
        current >= chosenInitialTime ? current : Math.min(current + add, chosenInitialTime);

    useEffect(() => {
        if (!running && activePlayer === null) {
            setBackButtonDisabled?.(false);
        } else {
            setBackButtonDisabled?.(true);
        }
    }, [running, activePlayer, setBackButtonDisabled]);


    // Load from localStorage on mount
    useEffect(() => {
        const saved = JSON.parse(localStorage.getItem('masterDuelTimerState'));
        const lastTimestamp = localStorage.getItem('masterDuelLastTimestamp');
        const pausedByClose = localStorage.getItem('masterDuelTimerPausedByClose') === 'true';

        if (saved) {
            setIsNew(false);
            setChosenInitialTime(saved.chosenInitialTime);
            setPlayer1Time(saved.player1Time);
            setPlayer2Time(saved.player2Time);
            setActivePlayer(saved.activePlayer);
            setPaused(saved.paused);
            setRunning(pausedByClose ? false : saved.running);

            if (lastTimestamp && saved.running && !pausedByClose) {
                const now = Date.now();
                const elapsed = Math.floor((now - Number(lastTimestamp)) / 1000);

                if (saved.activePlayer === 1) {
                    setPlayer1Time(Math.max(saved.player1Time - elapsed, 0));
                } else if (saved.activePlayer === 2) {
                    setPlayer2Time(Math.max(saved.player2Time - elapsed, 0));
                }
            }
        } else {
            setIsNew(true);
            setChosenInitialTime(defaultInitialTime);
            setPlayer1Time(defaultInitialTime);
            setPlayer2Time(defaultInitialTime);
        }

        setIsLoaded(true);
        localStorage.removeItem('masterDuelTimerPausedByClose');
    }, []);

    // Save to localStorage
    useEffect(() => {
        if (!isLoaded) return;
        const state = {
            chosenInitialTime,
            player1Time,
            player2Time,
            activePlayer,
            running,
            paused,
        };
        localStorage.setItem('masterDuelTimerState', JSON.stringify(state));

        if (running) {
            localStorage.setItem('masterDuelLastTimestamp', Date.now().toString());
        }
    }, [isLoaded, chosenInitialTime, player1Time, player2Time, activePlayer, running, paused]);

    // Pause on tab close
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (running) {
                localStorage.setItem('masterDuelTimerPausedByClose', 'true');
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [running]);

    // Adjust for tab visibility
    useEffect(() => {
        const handleVisibility = () => {
            if (!document.hidden && running) {
                const lastTimestamp = localStorage.getItem('masterDuelLastTimestamp');
                if (lastTimestamp) {
                    const now = Date.now();
                    const elapsed = Math.floor((now - Number(lastTimestamp)) / 1000);

                    if (activePlayer === 1) {
                        setPlayer1Time((prev) => Math.max(prev - elapsed, 0));
                    } else if (activePlayer === 2) {
                        setPlayer2Time((prev) => Math.max(prev - elapsed, 0));
                    }
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [running, activePlayer]);

    useEffect(() => {
        if (!running && activePlayer === null) {
            window.setShowGlobalMuteButton?.(true);
        } else {
            window.setShowGlobalMuteButton?.(false);
        }
    }, [running, activePlayer]);

    useEffect(() => {
        if (!running && activePlayer === null) {
            window.setShowGlobalTopButtons?.(true);
        } else {
            window.setShowGlobalTopButtons?.(false);
        }
    }, [running, activePlayer]);



    // Timer countdown
    useEffect(() => {
        let timer;
        if (running && !paused && activePlayer) {
            timer = setInterval(() => {
                if (activePlayer === 1 && player1Time > 0) {
                    setPlayer1Time((t) => t - 1);
                } else if (activePlayer === 2 && player2Time > 0) {
                    setPlayer2Time((t) => t - 1);
                }
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [running, paused, activePlayer, player1Time, player2Time]);

    const formatTime = (seconds) => {
        const min = Math.floor(seconds / 60);
        const sec = seconds % 60;
        return `${min}:${sec.toString().padStart(2, '0')}`;
    };

    const handleStartTurn = (player) => {
        if (player === 1) {
            setPlayer1Time((prev) => cappedAdd(prev, 60));
        } else {
            setPlayer2Time((prev) => cappedAdd(prev, 60));
        }
        setStartTurnUsed(true);  // mark both buttons as used
        setActivePlayer(player);
        setRunning(true);
        setPaused(false);
    };

    const handleChain = (player) => {
        const opponent = player === 1 ? 2 : 1;
        setActivePlayer(opponent);
        setRunning(true);
        setPaused(false);
    };

    const handleEndPhase = (player) => {
        const opponent = player === 1 ? 2 : 1;

        if (player === 1) {
            setPlayer1Time((prev) => cappedAdd(prev, 30));
        } else {
            setPlayer2Time((prev) => cappedAdd(prev, 30));
        }

        setActivePlayer(opponent);

        if (opponent === 1) {
            setPlayer1Time((prev) => cappedAdd(prev, 60));
        } else {
            setPlayer2Time((prev) => cappedAdd(prev, 60));
        }

        setRunning(true);
        setPaused(false);
    };

    const handlePause = () => setPaused((prev) => !prev);

    const confirmReset = () => {
        setShowResetConfirm(false);
        setRunning(false);
        setPaused(false);
        setActivePlayer(null);
        setChosenInitialTime(defaultInitialTime);
        setPlayer1Time(defaultInitialTime);
        setPlayer2Time(defaultInitialTime);
        setIsNew(true);
        setStartTurnUsed(false);
        localStorage.removeItem('masterDuelTimerState');
        localStorage.removeItem('masterDuelLastTimestamp');
        localStorage.removeItem('masterDuelTimerPausedByClose');
    };

    const toggleOperator = (player) => {
        if (player === 1) {
            setPlayer1Operator((prev) => (prev === "+" ? "-" : "+"));
        } else {
            setPlayer2Operator((prev) => (prev === "+" ? "-" : "+"));
        }
    };

    const updateLP = (player) => {
        if (player === 1) {
            const amount = parseInt(player1Input) || 0;
            if (player1Operator === "+") {
                setPlayer1LP((prev) => prev + amount);
            } else {
                setPlayer1LP((prev) => Math.max(0, prev - amount));
            }
            setPlayer1Input("");
        } else {
            const amount = parseInt(player2Input) || 0;
            if (player2Operator === "+") {
                setPlayer2LP((prev) => prev + amount);
            } else {
                setPlayer2LP((prev) => Math.max(0, prev - amount));
            }
            setPlayer2Input("");
        }
    };

    return (
        <div className="master-duel-ui">
            {!isLoaded ? (
                <div className="loading-screen">Loading...</div>
            ) : isNew ? (
                <div className="timer-config">
                    <h3>Master Duel Timer</h3>
                    <div className="time-options">
                        {[300, 480, 600].map((sec) => (
                            <button
                                key={sec}
                                className={`time-button ${chosenInitialTime === sec ? 'selected-time' : ''}`}
                                onClick={() => {
                                    setChosenInitialTime(sec);
                                    setPlayer1Time(sec);
                                    setPlayer2Time(sec);
                                }}
                            >
                                {sec / 60} min
                            </button>
                        ))}
                    </div>
                    <button
                        className="timer-start-btn"
                        onClick={() => {
                            setIsNew(false);
                            setRunning(true);
                        }}
                    >
                        Start Duel
                    </button>

                </div>
            ) : (
                <>
                    <div
                        className={`master-duel-player ${activePlayer === 1 ? 'master-duel-active' : 'master-duel-inactive'} flipped`}
                    >
                        <div className="master-duel-time">{formatTime(player1Time)}</div>
                        <div className="master-duel-separator"></div>
                        <div className="master-duel-controls">
                            <button className="start-turn-btn" onClick={() => handleStartTurn(1)} disabled={startTurnUsed}>Start Duel</button>
                            <button onClick={() => handleChain(1)}>Chain</button>
                            <button onClick={() => setPendingEndPhasePlayer(1)}>End Phase</button>
                        </div>
                        {/* <div className="lp-panel flipped">
                            <div>LP: {player1LP}</div>
                            <input
                                type="number"
                                value={player1Input}
                                onChange={(e) => setPlayer1Input(e.target.value)}
                            />
                            <button onClick={() => toggleOperator(1)}>{player1Operator}</button>
                            <button onClick={() => updateLP(1)}>Update LP</button>
                        </div> */}
                    </div>

                    <div className="master-duel-middle-controls">
                        <button onClick={handlePause}>
                            {paused ? '▶ Play' : 'Pause'}
                        </button>
                        <button onClick={() => setShowResetConfirm(true)}>Reset</button>
                        <button onClick={() => setShowCalculator(true)}>
                            Open Calculator
                        </button>
                    </div>

                    <div
                        className={`master-duel-player ${activePlayer === 2 ? 'master-duel-active' : 'master-duel-inactive'}`}
                    >
                        <div className="master-duel-time">{formatTime(player2Time)}</div>
                        <div className="master-duel-separator"></div>
                        <div className="master-duel-controls">
                            <button className="start-turn-btn" onClick={() => handleStartTurn(2)} disabled={startTurnUsed}>Start Duel</button>
                            <button onClick={() => handleChain(2)}>Chain</button>
                            <button onClick={() => setPendingEndPhasePlayer(2)}>End Phase</button>
                        </div>
                        {/* <div className="lp-panel">
                            <div>LP: {player2LP}</div>
                            <input
                                type="number"
                                value={player2Input}
                                onChange={(e) => setPlayer2Input(e.target.value)}
                            />
                            <button onClick={() => toggleOperator(2)}>{player2Operator}</button>
                            <button onClick={() => updateLP(2)}>Update LP</button>
                        </div> */}
                    </div>
                </>
            )}

            {showResetConfirm && (
                <div className="chess-modal-overlay">
                    <div className="chess-modal">
                        <h2>Reset Timer?</h2>
                        <div className="chess-modal-buttons">
                            <button className="chess-confirm" onClick={confirmReset}>
                                Confirm
                            </button>
                            <button className="chess-cancel" onClick={() => setShowResetConfirm(false)}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {pendingEndPhasePlayer && (
                <div className="chess-modal-overlay">
                    <div className="chess-modal">
                        <h2>Do you want to end your turn?</h2>
                        <div className="chess-modal-buttons">
                            <button
                                className="chess-confirm"
                                onClick={() => {
                                    handleEndPhase(pendingEndPhasePlayer);
                                    setPendingEndPhasePlayer(null);
                                }}
                            >
                                Confirm
                            </button>
                            <button
                                className="chess-cancel"
                                onClick={() => setPendingEndPhasePlayer(null)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <CalculatorModal show={showCalculator} onClose={() => setShowCalculator(false)} />
        </div>
    );
};

export default MasterDuelTimer;
